import React, { useState, useEffect } from "react";
import { Link, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Home, Users, Waves, GraduationCap, CreditCard, History, Settings2,
  LogOut, Bell, Menu, X, ExternalLink, PanelLeftClose, PanelLeftOpen, FileText, CalendarDays,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth, API } from "../App";

import AdminOverview from "./admin/AdminOverview";
import AdminLessons from "./admin/AdminLessons";
import AdminStudents from "./admin/AdminStudents";
import StudentProfile from "./StudentProfile";
import AdminSubscriptions from "./admin/AdminSubscriptions";
import AdminPacks from "./admin/AdminPacks";
import AdminLogs from "./admin/AdminLogs";
import AdminSettingsPage from "./admin/AdminSettingsPage";
import AdminContent from "./admin/AdminContent";
import CalendarPage from "./CalendarPage";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [weatherData, setWeatherData] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchNotifications();
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      const res = await fetch(`${API}/weather`);
      if (res.ok) setWeatherData(await res.json());
    } catch {}
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data || []);
      setUnreadCount((res.data || []).filter(n => !n.read).length);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isAdmin = user?.role === "admin";

  const allMenuItems = [
    { path: "/admin", icon: Home, label: "Dashboard", exact: true },
    { path: "/admin/lessons", icon: Waves, label: "Aulas" },
    { path: "/admin/calendar", icon: CalendarDays, label: "Calendário" },
    { path: "/admin/students", icon: Users, label: "Alunos" },
    { path: "/admin/subscriptions", icon: GraduationCap, label: "Subscrições", adminOnly: true },
    { path: "/admin/packs", icon: CreditCard, label: "Packs", adminOnly: true },
    { path: "/admin/content", icon: FileText, label: "Conteúdo Site", adminOnly: true },
    { path: "/admin/logs", icon: History, label: "Histórico", adminOnly: true },
    { path: "/admin/settings", icon: Settings2, label: "Configurações", adminOnly: true },
  ];

  const menuItems = allMenuItems.filter(item => !item.adminOnly || isAdmin);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div className={`p-4 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}>
        <img src="/assets/images/logo.png" alt="Apúlia Surf School" className={`${collapsed && !isMobile ? 'h-8 w-auto' : 'h-10 w-auto mb-6'}`} />
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {menuItems.map((item) => (
          <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}`} title={collapsed && !isMobile ? item.label : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium ${collapsed && !isMobile ? 'justify-center' : ''} ${isActive(item.path, item.exact) ? "bg-[#6697a4] text-white shadow-sm" : "text-[#475569] hover:bg-slate-100"}`}>
            <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
            {(!collapsed || isMobile) && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>
      <div className={`p-3 border-t border-slate-100`}>
        {collapsed && !isMobile ? (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#6697a4] text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback></Avatar>
            <button onClick={handleLogout} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Terminar Sessão" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 mb-3 px-1">
              <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#6697a4] text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#3d5a63] text-sm truncate">{user?.name}</p>
                <p className="text-xs text-[#94A3B8]">Admin</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid="admin-logout-btn">
              <LogOut className="mr-2 h-3.5 w-3.5" /> Terminar Sessão
            </Button>
          </>
        )}
      </div>
    </>
  );

  const NotificationsDropdown = ({ className = "" }) => (
    <div className={`absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden ${className}`} data-testid="admin-notifications-dropdown">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between">
        <p className="font-semibold text-sm text-[#3d5a63]">Notificações</p>
        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowNotifications(false)}><X className="h-3 w-3" /></Button>
      </div>
      <ScrollArea className="max-h-72">
        {notifications.length === 0 ? (
          <p className="p-4 text-sm text-[#94A3B8] text-center">Sem notificações</p>
        ) : (
          notifications.slice(0, 15).map(n => (
            <div key={n.notification_id} className={`p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${!n.read ? "bg-blue-50/50" : ""}`} onClick={() => { if (n.link) navigate(n.link); setShowNotifications(false); }}>
              <p className="text-sm font-medium text-[#3d5a63]">{n.title}</p>
              <p className="text-xs text-[#94A3B8] mt-0.5">{n.message}</p>
            </div>
          ))
        )}
      </ScrollArea>
    </div>
  );

  const NotificationBell = () => (
    <Button variant="ghost" size="sm" className="relative p-2" onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications && unreadCount > 0) handleMarkAllRead(); }} data-testid="admin-notifications-bell">
      <Bell className="h-5 w-5 text-[#475569]" />
      {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#e8927c] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount}</span>}
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-slate-100 bg-white fixed h-full z-30 transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-56'}`}>
        <SidebarContent />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 bg-white border border-slate-200 rounded-full p-1 shadow-sm hover:bg-slate-50 transition-colors" data-testid="sidebar-toggle">
          {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5 text-slate-500" /> : <PanelLeftClose className="h-3.5 w-3.5 text-slate-500" />}
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-100 flex items-center px-4 z-40">
        <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-[#3d5a63]" data-testid="mobile-admin-menu-btn"><Menu className="h-5 w-5" /></button>
        <img src="/assets/images/logo.png" alt="Apúlia Surf School" className="h-7 w-auto ml-2" />
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <NotificationBell />
            {showNotifications && <NotificationsDropdown className="w-72 sm:w-80" />}
          </div>
          <span className="text-xs font-medium text-[#94A3B8]">Admin</span>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="md:hidden fixed left-0 top-0 bottom-0 w-64 bg-white z-50 flex flex-col shadow-xl animate-fade-in">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <img src="/assets/images/logo.png" alt="Apúlia Surf School" className="h-8 w-auto" />
              <button onClick={() => setMobileOpen(false)} className="p-1 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <SidebarContent isMobile />
          </aside>
        </>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'md:ml-[68px]' : 'md:ml-56'} pt-14 md:pt-0`}>
        <div className="hidden md:flex items-center justify-end gap-3 px-8 py-3 bg-white border-b border-slate-100">
          <a href="/" className="text-sm text-[#6697a4] hover:text-[#3d5a63] flex items-center gap-1" data-testid="admin-home-link"><ExternalLink className="h-3.5 w-3.5" /> Site</a>
          <div className="relative">
            <NotificationBell />
            {showNotifications && <NotificationsDropdown className="w-80" />}
          </div>
        </div>
        <div className="p-4 md:p-8">
          <Routes>
            <Route path="/" element={<AdminOverview stats={stats} loading={loading} weatherData={weatherData} />} />
            <Route path="/lessons" element={<AdminLessons />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/students" element={<AdminStudents />} />
            <Route path="/students/:userId" element={<StudentProfile />} />
            {isAdmin && <Route path="/subscriptions" element={<AdminSubscriptions />} />}
            {isAdmin && <Route path="/packs" element={<AdminPacks />} />}
            {isAdmin && <Route path="/content" element={<AdminContent />} />}
            {isAdmin && <Route path="/logs" element={<AdminLogs />} />}
            {isAdmin && <Route path="/settings" element={<AdminSettingsPage />} />}
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
